import { isDefined } from 'common/TypeUtilities';
import { Dictionary, groupBy, head, keyBy, map, uniq } from 'lodash';
import { useMemo } from 'react';
import { ConsumerCartPublicShefFragment, useLocalCartShefsQuery } from 'src/gqlReactTypings.generated.d';
import { useSelector } from 'src/store';
import {
  selectAvailableCartItemCount,
  selectAvailableCartOrders,
  selectCart,
  selectFullLineItems,
  selectMultiShefCartSubtotal,
} from 'src/store/cart/selectors';

export const useConsumerCarts = (isEditingOrder = false) => {
  const { shefIds, cartItems, numItemsInCart, subtotal } = useSelector((state) => {
    const cart = selectCart(state, isEditingOrder);
    const lineItems = selectFullLineItems(cart);
    const orders = selectAvailableCartOrders(cart);
    const orderShefIds = orders.map((o) => o.shef.id);

    return {
      shefIds: uniq(map(lineItems, 'foodItem.userId')),
      cartItems: lineItems.map((lineItem) => ({
        quantity: lineItem.quantity,
        lineItem: lineItem.foodItem,
        shefId: lineItem.foodItem.userId,
      })),
      numItemsInCart: selectAvailableCartItemCount(cart),
      subtotal: selectMultiShefCartSubtotal(cart, orderShefIds),
    };
  });

  const { data } = useLocalCartShefsQuery({
    variables: {
      userIds: shefIds,
    },
  });

  const shefsById: Dictionary<ConsumerCartPublicShefFragment | undefined> = useMemo(() => {
    if (!data?.shefs) {
      return {};
    }
    const filteredShefs = data.shefs.filter((shef) => shef.shefProfile.cuisineRegion);
    return keyBy(filteredShefs, 'id');
  }, [data?.shefs]);

  const carts = useMemo(() => {
    const itemsByCart = groupBy(cartItems, 'shefId');

    return Object.keys(itemsByCart)
      .map((cartId) => {
        const cartLineItems = itemsByCart[cartId];
        const shefId = head(cartLineItems)?.shefId;
        if (!shefId) return undefined;

        const shef = shefsById[shefId];

        return {
          shefId,
          shef,
          cartLineItems,
        };
      })
      .filter(isDefined);
  }, [cartItems, shefsById]);

  return { carts, numItemsInCart, subtotal };
};
